import React, { useState, useEffect } from "react";
import axios from "../axios";
import { toast } from "react-toastify";
import ContactInput from "./ContactInput";
import AddressAutocomplete from "./AddressAutocomplete";
import "./CreateAdForm.css";
import { useNavigate } from "react-router-dom";
import BackLink from "./BackLink";

function normalizeCoord(val) {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (Number.isNaN(n)) return "";
  // обрезаем до 6 знаков: 38.123456789 -> "38.123457"
  return (Math.round(n * 1e6) / 1e6).toFixed(6);
}

function CreateAdForm({ user, copyFromId = null, initialCategoryId = null }) {
  const navigate = useNavigate();

// построить путь от корня до категории
function buildCategoryPath(list, id) {
  function dfs(node, targetId, trail) {
    const next = [...trail, node];
    if (String(node.id) === String(targetId)) return next;
    if (node.subcategories?.length) {
      for (const ch of node.subcategories) {
        const r = dfs(ch, targetId, next);
        if (r) return r;
      }
    }
    return null;
  }
  for (const root of list) {
    const r = dfs(root, id, []);
    if (r) return r;
  }
  return [];
}


  // === состояния ===
  const [phones, setPhones] = useState([]);
  const [contactPhone, setContactPhone] = useState("");
  const [showNewPhoneBlock, setShowNewPhoneBlock] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [countryData, setCountryData] = useState({ code: "+30", flag: "🇬🇷" });
  const [showManagePhones, setShowManagePhones] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mainCategory, setMainCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [subSubCategory, setSubSubCategory] = useState("");
  const selectedCatId = subSubCategory || subCategory || mainCategory || "";
  const selectedPath = selectedCatId ? buildCategoryPath(categories, selectedCatId) : [];
  const [resendTimer, setResendTimer] = useState(0);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [priceError, setPriceError] = useState("");

  const [status, setStatus] = useState(""); // Νέο / Μεταχειρισμένο
  const [mileage, setMileage] = useState("");

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [addr, setAddr] = useState({
    address_line: "",
    city: "",
    latitude: null,
    longitude: null,
  });  

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 1) грузим телефоны один раз
  useEffect(() => {
    axios
      .get("/api/phone-numbers/")
      .then((res) => setPhones(res.data || []))
      .catch(() => setPhones([]));
  }, []);

  // 2) если телефоны пришли и номер ещё не выбран — авто-подстановка первого
  useEffect(() => {
    if (phones.length && !contactPhone) {
      setContactPhone(phones[0].phone);
      setPhoneVerified(!!phones[0].is_verified);
    }
  }, [phones, contactPhone]);

  // === загрузка категорий ===
  useEffect(() => {
    axios
      .get("/api/categories/")
      .then((res) => setCategories(res.data))
      .catch(() => setError("Σφάλμα φόρτωσης κατηγοριών"));
  }, []);


  useEffect(() => {
    if (!initialCategoryId || !categories.length) return;

    const getPathById = (tree, targetId) => {
      const dfs = (node, trail = []) => {
        const next = [...trail, node];

        if (String(node.id) === String(targetId)) {
          return next;
        }

        for (const child of node.subcategories || []) {
          const result = dfs(child, next);

          if (result) {
            return result;
          }
        }

        return null;
      };

      for (const root of tree || []) {
        const result = dfs(root, []);

        if (result) {
          return result;
        }
      }

      return [];
    };

    const path = getPathById(categories, initialCategoryId);

    if (!path.length) return;

    const [lvl1, lvl2, lvl3] = path;

    setMainCategory(lvl1?.id ? String(lvl1.id) : "");
    setSubCategory(lvl2?.id ? String(lvl2.id) : "");
    setSubSubCategory(lvl3?.id ? String(lvl3.id) : "");
  }, [initialCategoryId, categories]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => {
      setResendTimer((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  // === обработка изображений с превью ===
  const handleImageChange = (e) => {
    const picked = Array.from(e.target.files || []);

    // анти-дубликаты: сравниваем по имени+размеру+lastModified
    const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
    const existingKeys = new Set(images.map(key));

    const fresh = picked.filter((f) => !existingKeys.has(key(f)));

    // общий лимит (например, 10)
    const merged = [...images, ...fresh].slice(0, 10);
    setImages(merged);

    // превью для только что добавленных файлов
    const readers = fresh.slice(0, Math.max(0, 10 - images.length)).map(
      (file) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result);
          fr.readAsDataURL(file);
        })
    );

    Promise.all(readers).then((urls) => {
      // добавляем к существующим превью
      setImagePreviews((prev) => [...prev, ...urls].slice(0, 10));
    });

    // очистим value, чтобы повторный выбор тех же файлов снова триггерил onChange
    e.target.value = "";
  };

  const removeImageAt = (idx) => {
    setImages((arr) => arr.filter((_, i) => i !== idx));
    setImagePreviews((arr) => arr.filter((_, i) => i !== idx));
  };

  // === отправка кода ===
  const sendCode = async () => {
    if (resendTimer > 0 || loading) return; // защита от повторных кликов/таймера
    setConfirmError("");

    if (!newPhone.trim()) {
      setConfirmError("📱 Πρέπει να εισάγετε αριθμό τηλεφώνου.");
      return;
    }

    try {
      setLoading(true);
      await axios.post("/api/send-code/", {
        contact: newPhone,
        password: "temporary",
        mode: "verify",
      });
      toast.info("📨 Ο κωδικός στάλθηκε!");
      setCodeSent(true);
      setResendTimer(60); // ⏱ старт таймера на 60 сек
    } catch {
      setConfirmError("❌ Σφάλμα αποστολής.");
    } finally {
      setLoading(false);
    }
  };

  // === подтверждение номера ===
  const verifyPhoneNumber = async () => {
    try {
      setLoading(true);
      await axios.post("/api/verify-phone/", { code, phone: newPhone });
      toast.success("✅ Ο αριθμός τηλεφώνου επιβεβαιώθηκε!");
      const updated = await axios.get("/api/phone-numbers/");
      setPhones(updated.data || []);
      setShowNewPhoneBlock(false);
      setContactPhone(newPhone);
      setPhoneVerified(true);
      setCode("");
      setCodeSent(false);
    } catch (err) {
      setConfirmError(err.response?.data?.error || "Σφάλμα επιβεβαίωσης.");
    } finally {
      setLoading(false);
    }
  };

  // === удаление номера ===
  const handleDeletePhone = async (id) => {
    try {
      await axios.delete(`/api/phone-numbers/${id}/`);
      const res = await axios.get("/api/phone-numbers/");
      setPhones(res.data || []);
      toast.success("✅ Ο αριθμός διαγράφηκε.");
    } catch {
      toast.error("❌ Δεν ήταν δυνατή η διαγραφή του αριθμού.");
    }
  };


  // === валидация формы ===
  const validateForm = () => {
    if (!title.trim()) return "Το πεδίο τίτλος είναι υποχρεωτικό.";
    if (!description.trim()) return "Το πεδίο περιγραφή είναι υποχρεωτικό.";
    if (!price || isNaN(price) || Number(price) <= 0)
      return "Παρακαλώ εισάγετε έγκυρη τιμή.";
    if (!mainCategory) return "Η κατηγορία είναι υποχρεωτική.";
    if (!addr.address_line) return "Το πεδίο τοποθεσίας είναι υποχρεωτικό.";
    if (!images.length) return "Η προσθήκη εικόνας είναι υποχρεωτική.";
    return null;
  };

  // === отправка объявления ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      setError("");
      window.dispatchEvent(
        new CustomEvent("open-login-modal", {
          detail: { message: "Παρακαλώ συνδεθείτε για να δημιουργήσετε μια αγγελία." },
        })
      );
      return;
    }

    const err = validateForm();
    if (err) {
      setError(err);
      return;
    }

    if (!phoneVerified) {
      toast.error("📞 Ο αριθμός δεν έχει επιβεβαιωθεί.");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("price", price);
    const latStr = normalizeCoord(addr.latitude);
    const lonStr = normalizeCoord(addr.longitude);

    formData.append("location", (addr.address_line || "").trim());
    formData.append("address_line", addr.address_line || "");
    formData.append("city", addr.city || "");
    if (latStr) formData.append("latitude", latStr);
    if (lonStr) formData.append("longitude", lonStr);
    formData.append("contact_phone", contactPhone);

    const finalCategory = subSubCategory || subCategory || mainCategory;
    formData.append("category", finalCategory);

    if (mainCategoryName() === "Οχήματα" && status) {
      const info =
        status === "used"
          ? `Κατάσταση: Μεταχειρισμένο, ${mileage || 0} χλμ.\n${description}`
          : `Κατάσταση: Νέο\n${description}`;
      formData.set("description", info);
    }

    images.forEach((file) => formData.append("images", file));

    try {
      const res = await axios.post("/api/ads/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 201) {
        setSuccess(true);
        toast.success("✅ Η αγγελία δημιουργήθηκε!");
        setTimeout(() => navigate("/"), 2000);
      }
    } catch (err) {
      // 👇 Посмотрим, что именно вернул DRF
      console.error("POST /api/ads/ error:", err.response?.status, err.response?.data);
      const d = err.response?.data || {};
      // DRF может вернуть: {detail: "..."} или {field: ["..."]}
      const msg =
        d.detail ||
        d.contact_phone?.[0] ||
        d.category?.[0] ||
        d.price?.[0] ||
        d.title?.[0] ||
        d.description?.[0] ||
        "Σφάλμα δημιουργίας αγγελίας.";
      setError(msg);
    }
  };

  const mainCategoryName = () => {
    const main = categories.find((c) => c.id === Number(mainCategory));
    return main?.name || "";
  };


  return (
    <div className="create-ad-page">
      <div className="create-header create-header--with-back">
        <div className="create-header-left">
          <BackLink />
        </div>

        <div className="create-header-center">
          <h1 className="create-title">Δημιουργία Αγγελίας</h1>

          {selectedPath.length > 0 && (
            <div className="create-breadcrumbs">
              {selectedPath.map((n, i) => (
                <button
                  key={n.id}
                  type="button"
                  className="crumb"
                  onClick={() => navigate("/create")}
                >
                  {i > 0 && <span className="crumb-sep">›</span>}
                  <span>{n.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="create-header-right" />
      </div>

      <form onSubmit={handleSubmit} className="create-ad-form">
        {/* === телефоны (выпадающий список) === */}
        <div className="phone-block">
          <label className="phone-block-title">📱 Τηλέφωνο για επικοινωνία</label>

          <select
            value={contactPhone}
            onChange={(e) => {
              const val = e.target.value;
              setContactPhone(val);
              const obj = phones.find((p) => p.phone === val);
              setPhoneVerified(obj?.is_verified || false);
            }}
            className="styled-select"
            required
          >
            <option value="">— Επιλέξτε αποθηκευμένο αριθμό —</option>
            {phones.map((p) => (
              <option key={p.id} value={p.phone}>
                {p.phone} {p.is_verified ? "✅" : "❌"}
              </option>
            ))}
          </select>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setShowNewPhoneBlock(true);
                setShowManagePhones(false);
              }}
            >
              ➕ Προσθήκη νέου αριθμού
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowManagePhones((v) => !v)}
            >
              🗂 Διαχείριση αποθηκευμένων
            </button>
          </div>

          {/* Добавление + подтверждение нового номера (как раньше) */}
          {showNewPhoneBlock && (
            <div className="new-phone-form">
              <label>Νέος αριθμός:</label>
              <ContactInput
                value={newPhone}
                onChange={(val) => setNewPhone(val)}
                countryData={countryData}
                setCountryData={setCountryData}
                placeholder="Τηλέφωνο"
              />

              {!codeSent ? (
                <button type="button" className="btn btn-outline" onClick={sendCode}>
                  Αποστολή κωδικού
                </button>
              ) : (
                <>
                  <input
                    className="input-lg"
                    placeholder="Κωδικός επιβεβαίωσης"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <div className="verify-buttons">
                    <button type="button" className="btn btn-primary" onClick={verifyPhoneNumber}>
                      Επιβεβαίωση
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={sendCode}
                      disabled={resendTimer > 0 || loading}
                      title={resendTimer > 0 ? `Μπορείτε ξανά σε ${resendTimer}s` : "Αποστολή ξανά"}
                    >
                      {resendTimer > 0 ? `Αποστολή ξανά (${resendTimer})` : "Αποστολή ξανά"}
                    </button>
                  </div>
                </>
              )}

              {confirmError && <p className="error-message">{confirmError}</p>}
            </div>
          )}

          {/* Управление: показываем список с удалением ТОЛЬКО когда открыто */}
          {showManagePhones && (
            <div style={{ marginTop: 8 }}>
              {phones.length === 0 ? (
                <p className="no-phone-text">Δεν υπάρχουν αποθηκευμένοι αριθμοί.</p>
              ) : (
                phones.map((p) => (
                  <div key={p.id} className="phone-item">
                    <div
                      className="phone-info"
                      onClick={() => {
                        setContactPhone(p.phone);
                        setPhoneVerified(!!p.is_verified);
                      }}
                    >
                      {p.phone} {p.is_verified ? "✅" : "❌"}
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger btn-icon"
                      onClick={() => handleDeletePhone(p.id)}
                      title="Διαγραφή"
                    >
                      ✖
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>


        {/* === блок состояния для автомобилей === */}
        {mainCategoryName() === "Οχήματα" && (
          <div className="status-block">
            <label>
              🚗 <strong>Κατάσταση</strong>
            </label>

            <div className="status-options">
              <label className="radio-chip">
                <input
                  type="radio"
                  name="status"
                  value="new"
                  checked={status === "new"}
                  onChange={(e) => setStatus(e.target.value)}
                />
                <span className="chip">Νέο</span>
              </label>

              <label className="radio-chip">
                <input
                  type="radio"
                  name="status"
                  value="used"
                  checked={status === "used"}
                  onChange={(e) => setStatus(e.target.value)}
                />
                <span className="chip">Μεταχειρισμένο</span>
              </label>
            </div>

            {status === "used" && (
              <input
                className="auto-mileage"
                type="number"
                placeholder="Προσθέστε χιλιόμετρα"
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
              />
            )}
          </div>
        )}

        {/* === поля объявления === */}
        <input
          className="input-lg"
          placeholder="Τίτλος"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          placeholder="Περιγραφή"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div className="price-input">
          <span>€</span>
          <input
            type="text"
            placeholder="Τιμή σε ευρώ"
            value={price}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "");
              if (digitsOnly.length > 8) {
                setPrice(digitsOnly.slice(0, 8));
                setPriceError("Μέγιστη επιτρεπτή τιμή: 99 999 999 €");
              } else {
                setPrice(digitsOnly);
                setPriceError("");
              }
            }}
          />
        </div>
        {priceError && <p className="error-message">{priceError}</p>}

        <AddressAutocomplete
          value={addr.address_line || ""}
          placeholder="Τοποθεσία (πληκτρολογήστε και επιλέξτε)"
          onSelect={(picked) => {
            setAddr({
              ...picked,
              latitude:
                picked?.latitude != null ? normalizeCoord(picked.latitude) : "",
              longitude:
                picked?.longitude != null ? normalizeCoord(picked.longitude) : "",
            });
          }}
        />

        {addr.city && (
          <div className="selected-city">
            Πόλη: <strong>{addr.city}</strong>
          </div>
        )}

        {/* === фото === */}
        <label>Φωτογραφίες</label>
        <input
          type="file"
          name="images"
          accept="image/*"
          multiple
          onChange={handleImageChange}
        />
        {imagePreviews.length > 0 && (
          <div className="image-previews">
            {imagePreviews.map((src, i) => (
              <div key={i} className="preview-item">
                <img src={src} alt={`preview-${i}`} className="image-preview" />
                <button
                  type="button"
                  className="preview-remove"
                  onClick={() => removeImageAt(i)}
                  aria-label="Διαγραφή εικόνας"
                  title="Διαγραφή"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* === кнопка публикации === */}
        <button type="submit" className="btn btn-primary btn-lg" disabled={!phoneVerified || loading}>
          Δημοσίευση αγγελίας
        </button>
        {success && (
          <p style={{ color: "green" }}>✅ Η αγγελία δημιουργήθηκε!</p>
        )}
        {error && <p className="error-message">{error}</p>}
      </form>
    </div>
  );
}

export default CreateAdForm;
